/**
 * Industry UI configs.
 *
 * The frontend source of truth for per-industry shape: terminology,
 * dashboard quick actions, accent color, icon emoji. Mirrors the backend
 * INDUSTRY_TYPES catalog (same IDs).
 *
 * Why frontend-canonical:
 *   - Used on every page render → round-trip per render is wasteful
 *   - Pure UI concern: backend doesn't care if "Products" is called "Menu"
 *   - Editing terminology shouldn't require a backend deploy
 *
 * Adding/changing an industry:
 *   - Mirror the ID in eaze-backend/src/industries/industry.constants.js
 *   - Add the full UI config here
 */
import type { LucideIcon } from "lucide-react";
import {
  ShoppingBag,
  UtensilsCrossed,
  Scissors,
  Pill,
  Wrench,
  Truck,
  Briefcase,
  Dumbbell,
  Layers,
  Plus,
  PlusCircle,
  Receipt,
  AlertTriangle,
  ListChecks,
  Boxes,
  CalendarCheck,
  Users,
  Building2,
  Factory,
  PackagePlus,
  FileText,
} from "lucide-react";

export type IndustryId =
  | "retail"
  | "restaurant"
  | "salon"
  | "pharmacy"
  | "hardware"
  | "wholesale"
  | "services"
  | "gym"
  | "construction"
  | "manufacturing"
  | "other";

export interface IndustryTerms {
  /** Singular: "Product" / "Menu item" / "Service" */
  product: string;
  /** Plural: "Products" / "Menu" / "Services" */
  products: string;
  /** Singular: "Customer" / "Guest" / "Client" / "Patient" / "Member" */
  customer: string;
  /** Plural: "Customers" / "Guests" / etc. */
  customers: string;
  /** The action a cashier takes: "New sale" / "New order" / "New appointment" */
  saleAction: string;
  /** Label for low-stock alerts: "Low stock" / "Running low" / "Supplies low" */
  lowStockLabel: string;
  /** Page title for /products: "Products" / "Menu" / "Services catalog" */
  productsPageTitle: string;
  /** Page title for /orders: "Orders" / "Sales" / "Appointments" */
  ordersPageTitle: string;

  /**
   * Scheduling terminology — OPTIONAL. Present only for industries that
   * schedule future work (salon appointments, restaurant reservations,
   * service bookings, gym classes, wholesale deliveries, clinic visits).
   * Its ABSENCE is the signal that an industry doesn't schedule (retail,
   * hardware, pharmacy walk-in) — the registry hides the Schedule surface
   * for those. Terminology + capability travel together.
   */
  schedule?: {
    /** Surface label / page title: "Appointments" / "Reservations" / "Deliveries" */
    title: string;
    /** Primary action: "Book appointment" / "New reservation" / "Schedule delivery" */
    newAction: string;
    /** Singular noun for one row: "appointment" / "reservation" / "delivery" */
    item: string;
    /** Empty-day message: "No appointments scheduled" */
    emptyState: string;
  };
}


/** Only the string-valued keys of IndustryTerms (excludes nested terms like
 * `schedule`). Used wherever a flat string label is required. */
export type StringTermKey = {
  [K in keyof IndustryTerms]-?: IndustryTerms[K] extends string ? K : never;
}[keyof IndustryTerms];



export interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  /**
   * Visual accent: primary = filled green button; outline = bordered.
   * The first action in the list is typically primary; the rest outline.
   */
  variant?: "primary" | "outline";
}

export interface IndustryConfig {
  id: IndustryId;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  /** Emoji used in narrow contexts (badge, picker) */
  emoji: string;
  description: string;
  /** Brand-compatible accent. Falls back to Eaze green. */
  accentColor: string;
  terms: IndustryTerms;
  quickActions: QuickAction[];
}

// ─── Configs ──────────────────────────────────────────────────────

export const INDUSTRIES: Record<IndustryId, IndustryConfig> = {
  retail: {
    id: "retail",
    label: "General retail",
    shortLabel: "Retail",
    icon: ShoppingBag,
    emoji: "🛍️",
    description: "Shops, supermarkets, convenience stores.",
    accentColor: "#4CAF50",
    terms: {
      product: "Product",
      products: "Products",
      customer: "Customer",
      customers: "Customers",
      saleAction: "New sale",
      lowStockLabel: "Low stock",
      productsPageTitle: "Products",
      ordersPageTitle: "Orders",
      schedule: {
        title: "Appointments",
        newAction: "Book appointment",
        item: "appointment",
        emptyState: "No appointments scheduled",
      },
    },
    quickActions: [
      {
        id: "new_sale",
        label: "New sale",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "add_product",
        label: "Add product",
        icon: PlusCircle,
        to: "/products?action=new",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
      {
        id: "low_stock",
        label: "Low stock",
        icon: AlertTriangle,
        to: "/low-stock",
      },
    ],
  },

  restaurant: {
    id: "restaurant",
    label: "Restaurant / food",
    shortLabel: "Restaurant",
    icon: UtensilsCrossed,
    emoji: "🍽️",
    description: "Restaurants, cafes, food vendors, eateries.",
    accentColor: "#D97706",
    terms: {
      product: "Menu item",
      products: "Menu",
      customer: "Guest",
      customers: "Guests",
      saleAction: "New order",
      lowStockLabel: "Running low",
      productsPageTitle: "Menu",
      ordersPageTitle: "Orders",
      schedule: {
        title: "Reservations",
        newAction: "New reservation",
        item: "reservation",
        emptyState: "No reservations for this day",
      },
    },
    quickActions: [
      {
        id: "new_order",
        label: "New order",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "update_menu",
        label: "Update menu",
        icon: PlusCircle,
        to: "/products?action=new",
      },
      {
        id: "todays_orders",
        label: "Today's orders",
        icon: ListChecks,
        to: "/orders?range=today",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  salon: {
    id: "salon",
    label: "Salon / beauty",
    shortLabel: "Salon",
    icon: Scissors,
    emoji: "💇",
    description: "Salons, barbershops, beauty parlors, spas.",
    accentColor: "#EC4899",
    terms: {
      product: "Service",
      products: "Services",
      customer: "Client",
      customers: "Clients",
      saleAction: "New appointment",
      lowStockLabel: "Supplies low",
      productsPageTitle: "Services",
      ordersPageTitle: "Sales",
    },
    quickActions: [
      {
        id: "new_appointment",
        label: "New appointment",
        icon: CalendarCheck,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "add_service",
        label: "Add service",
        icon: PlusCircle,
        to: "/products?action=new",
      },
      {
        id: "client_list",
        label: "Client list",
        icon: Users,
        to: "/customers",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  pharmacy: {
    id: "pharmacy",
    label: "Pharmacy / pharmaceutical",
    shortLabel: "Pharmacy",
    icon: Pill,
    emoji: "💊",
    description: "Pharmacies, chemists, drugstores.",
    accentColor: "#0EA5E9",
    terms: {
      product: "Medicine",
      products: "Medicines",
      customer: "Patient",
      customers: "Patients",
      saleAction: "New sale",
      lowStockLabel: "Stock low",
      productsPageTitle: "Medicines",
      ordersPageTitle: "Dispensary",
    },
    quickActions: [
      {
        id: "new_sale",
        label: "New sale",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "receive_stock",
        label: "Receive stock",
        icon: PackagePlus,
        to: "/low-stock",
      },
      {
        id: "low_stock",
        label: "Stock alerts",
        icon: AlertTriangle,
        to: "/low-stock",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  hardware: {
    id: "hardware",
    label: "Hardware / construction",
    shortLabel: "Hardware",
    icon: Wrench,
    emoji: "🔨",
    description: "Hardware stores, building materials, construction supply.",
    accentColor: "#92400E",
    terms: {
      product: "Item",
      products: "Items",
      customer: "Customer",
      customers: "Customers",
      saleAction: "New sale",
      lowStockLabel: "Stock low",
      productsPageTitle: "Items",
      ordersPageTitle: "Sales",
    },
    quickActions: [
      {
        id: "new_sale",
        label: "New sale",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "receive_stock",
        label: "Receive stock",
        icon: PackagePlus,
        to: "/low-stock",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
      {
        id: "outstanding",
        label: "Outstanding",
        icon: FileText,
        to: "/orders?status=partial",
      },
    ],
  },

  construction: {
    id: "construction",
    label: "Construction / real estate",
    shortLabel: "Construction",
    icon: Building2,
    emoji: "🏗️",
    description: "Contractors, developers, project-based builders.",
    accentColor: "#B45309",
    terms: {
      product: "Material",
      products: "Materials",
      customer: "Client",
      customers: "Clients",
      saleAction: "New sale",
      lowStockLabel: "Stock low",
      productsPageTitle: "Materials",
      ordersPageTitle: "Invoices",
      // no `schedule` — construction uses project milestones, not the
      // scheduled-order rail, so core.schedule stays hidden for it.
    },
    quickActions: [
      {
        id: "projects",
        label: "Projects",
        icon: Building2,
        to: "/projects",
        variant: "primary",
      },
      { id: "materials", label: "Materials", icon: Boxes, to: "/products" },
      { id: "clients", label: "Clients", icon: Users, to: "/customers" },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  manufacturing: {
    id: "manufacturing",
    label: "Manufacturing",
    shortLabel: "Manufacturing",
    icon: Factory,
    emoji: "🏭",
    description: "Factories and producers: drinks, food, building materials.",
    accentColor: "#475569",
    terms: {
      product: "Product",
      products: "Products",
      customer: "Customer",
      customers: "Customers",
      saleAction: "New sale",
      lowStockLabel: "Stock low",
      productsPageTitle: "Products",
      ordersPageTitle: "Sales",
    },
    quickActions: [
      { id: "new_sale", label: "New sale", icon: Plus, to: "/cashier", variant: "primary" },
      { id: "receive_stock", label: "Receive stock", icon: PackagePlus, to: "/low-stock" },
      { id: "low_stock", label: "Stock alerts", icon: AlertTriangle, to: "/low-stock" },
      { id: "record_expense", label: "Record expense", icon: Receipt, to: "/expenditures?action=new" },
    ],
  },

  wholesale: {
    id: "wholesale",
    label: "Wholesale / distribution",
    shortLabel: "Wholesale",
    icon: Truck,
    emoji: "📦",
    description: "Wholesale distributors, bulk suppliers.",
    accentColor: "#7C3AED",
    terms: {
      product: "Item",
      products: "Items",
      customer: "Buyer",
      customers: "Buyers",
      saleAction: "New order",
      lowStockLabel: "Stock low",
      productsPageTitle: "Items",
      ordersPageTitle: "Orders",
      schedule: {
        title: "Deliveries",
        newAction: "Schedule delivery",
        item: "delivery",
        emptyState: "No deliveries scheduled",
      },
    },
    quickActions: [
      {
        id: "new_order",
        label: "New order",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "receive_stock",
        label: "Receive stock",
        icon: PackagePlus,
        to: "/low-stock",
      },
      { id: "transfer", label: "Transfer stock", icon: Truck, to: "/group" },
      {
        id: "outstanding",
        label: "Outstanding",
        icon: FileText,
        to: "/orders?status=partial",
      },
    ],
  },

  services: {
    id: "services",
    label: "Services",
    shortLabel: "Services",
    icon: Briefcase,
    emoji: "💼",
    description: "Consulting, repairs, professional services.",
    accentColor: "#0F766E",
    terms: {
      product: "Service",
      products: "Services",
      customer: "Client",
      customers: "Clients",
      saleAction: "New job",
      lowStockLabel: "Materials low",
      productsPageTitle: "Services",
      ordersPageTitle: "Jobs",
      schedule: {
        title: "Bookings",
        newAction: "New booking",
        item: "booking",
        emptyState: "No bookings scheduled",
      },
    },
    quickActions: [
      {
        id: "new_job",
        label: "New job",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "add_service",
        label: "Add service",
        icon: PlusCircle,
        to: "/products?action=new",
      },
      {
        id: "outstanding",
        label: "Outstanding",
        icon: FileText,
        to: "/orders?status=partial",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  gym: {
    id: "gym",
    label: "Gym / fitness",
    shortLabel: "Gym",
    icon: Dumbbell,
    emoji: "🏋️",
    description: "Gyms, fitness centers, membership-based businesses.",
    accentColor: "#DC2626",
    terms: {
      product: "Membership",
      products: "Memberships",
      customer: "Member",
      customers: "Members",
      saleAction: "Check in",
      lowStockLabel: "Supplies low",
      productsPageTitle: "Memberships",
      ordersPageTitle: "Check-ins",
      schedule: {
        title: "Classes",
        newAction: "Schedule class",
        item: "class",
        emptyState: "No classes scheduled",
      },
    },
    quickActions: [
      {
        id: "check_in",
        label: "Check in",
        icon: CalendarCheck,
        to: "/cashier/checkin",
        variant: "primary",
      },
      { id: "manage_members", label: "Members", icon: Users, to: "/customers" },
      { id: "memberships", label: "Memberships", icon: Boxes, to: "/products" },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
    ],
  },

  other: {
    id: "other",
    label: "Other",
    shortLabel: "Other",
    icon: Layers,
    emoji: "📋",
    description: "Anything else — uses generic terminology.",
    accentColor: "#475569",
    terms: {
      product: "Product",
      products: "Products",
      customer: "Customer",
      customers: "Customers",
      saleAction: "New sale",
      lowStockLabel: "Low stock",
      productsPageTitle: "Products",
      ordersPageTitle: "Orders",
    },
    quickActions: [
      {
        id: "new_sale",
        label: "New sale",
        icon: Plus,
        to: "/cashier",
        variant: "primary",
      },
      {
        id: "add_product",
        label: "Add product",
        icon: PlusCircle,
        to: "/products?action=new",
      },
      {
        id: "record_expense",
        label: "Record expense",
        icon: Receipt,
        to: "/expenditures?action=new",
      },
      {
        id: "low_stock",
        label: "Low stock",
        icon: AlertTriangle,
        to: "/low-stock",
      },
    ],
  },
};

export const DEFAULT_INDUSTRY: IndustryId = "retail";

export const INDUSTRY_LIST: IndustryConfig[] = Object.values(INDUSTRIES);

export function getIndustry(id: string | null | undefined): IndustryConfig {
  if (!id) return INDUSTRIES[DEFAULT_INDUSTRY];
  const found = (INDUSTRIES as Record<string, IndustryConfig | undefined>)[id];
  return found || INDUSTRIES[DEFAULT_INDUSTRY];
}

export function isValidIndustryId(
  id: string | null | undefined,
): id is IndustryId {
  if (!id) return false;
  return id in INDUSTRIES;
}
